/** @flow */

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import moment from "moment";
import { formatDateTime, transformSecondsInHours } from "@transformers/date";
import actionCreators from "@modules/profile/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import type { PaginationExtendedType, PropertiesSchema } from "@types";
import type { ProfileUser } from "@modules/account/types";
import type { State } from "@store/reducers";
import * as selectors from "@modules/profile/selectors";
import * as sharedSelectors from "@modules/shared/selectors/employee-absences";
import { ConfirmationModal, ContextMenu, MenuItem, GSGrid, Pagination } from "@components";
import { AbsencesFilter } from "@modules/shared/components";
import { AddMyAbsencesModal } from "@modules/profile/components";
import { canOpenAbsence } from "@modules/profile/selectors";

type OwnProps = $Exact<{
  employeeId: number,
  clientId: number,
  user: ProfileUser,
}>;

type PropsFromState = $Exact<{
  myAbsences: Array<EmployeeAbsence>,
  pagination: PaginationExtendedType,
  absencesSchema: ?PropertiesSchema,
  showAddModal: boolean,
  showCloseModal: boolean,
  showReOpenModal: boolean,
  showCancelModal: boolean,
  currentAbsence: Object | null,
  isEdit: boolean,
  canOpenAbsence: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getMyAbsences: typeof actionCreators.GetMyAbsences,
  getEmployeeAbsencesSchema: typeof sharedActionCreators.GetEmployeeAbsencesSchema,
  setShowAddModal: typeof actionCreators.SetShowAddMyAbsencesModal,
  setShowCloseModal: typeof actionCreators.SetShowCloseMyAbsencesModal,
  setShowReOpenModal: typeof actionCreators.SetShowReOpenMyAbsencesModal,
  setAbsenceStatus: typeof actionCreators.SetAbsenceStatus,
  setShowCancelModal: typeof actionCreators.SetShowCancelMyAbsencesModal,
  setMyCurrentAbsence: typeof actionCreators.SetMyCurrentAbsence,
  updateDraft: typeof actionCreators.SetMyAbsenceDraft,
  getTimeLost: typeof actionCreators.GetMyAbsenceDaysLost,
  exportMyAbsences: typeof actionCreators.ExportMyAbsences,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

function MyAbsences(props: Props) {
  const { t, clientId, user, myAbsences, pagination } = props;
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  // fetch absences
  useEffect(() => {
    fetchMyAbsences();
    // retrieve employee absence schema which is passed to the filter component
    //from where the absence type options will be extracted and displayed in Absence Type filter selector
    props.getEmployeeAbsencesSchema({
      clientId: props.clientId,
      employeeId: user.id,
    });
  }, []);

  const fetchMyAbsences = (page?: number) => {
    props.getMyAbsences({
      clientId: clientId,
      employeeId: user.id,
      page,
    });
  };

  function columnDefs() {
    return [
      {
        key: "id",
        name: t("ABSENCE_ID"),
        frozen: "left",
      },
      {
        key: "start",
        name: t("START"),
        renderCell: ({ row }: Object) => formatDateTime(row.startsAt) || "",
      },
      {
        key: "last_absence_date",
        name: t("LAST_ABSENT_DAY"),
        width: 100,
        renderCell: ({ row }: Object) =>
          row.lastDayOfAbsence ? moment(row.lastDayOfAbsence).format("DD/MM/YYYY") : "",
      },
      {
        key: "rtw_date",
        name: t("RTW_DATE"),
        renderCell: ({ row }: Object) => formatDateTime(row.rtwAt) || "",
      },
      {
        key: "working_days",
        name: t("WORKING_DAYS"),
        renderCell: ({ row }: Object) => row.workingDaysLost || "",
      },
      {
        key: "hours_lost",
        name: t("HOURS_LOST"),
        renderCell: ({ row }: Object) =>
          transformSecondsInHours(row.workingTimeLostInSeconds) || "",
      },
      {
        key: "calendar_days",
        name: t("CALENDAR_DAYS"),
        renderCell: ({ row }: Object) => row.calendarDaysLost || "",
      },
      {
        key: "absence_type",
        name: t("ABSENCE_TYPE"),
        renderCell: ({ row }: *) => GSGrid.render.statusPill(t(row.absenceType)),
      },
      {
        key: "absence_reason",
        name: t("ABSENCE_REASON"),
        renderCell: ({ row }: Object) => (row.absenceReason || {}).name || t("CONSENT_WITHHELD"),
        align: "left",
      },

      {
        key: "status",
        name: t("STATUS"),
        renderCell: ({ row }: *) => GSGrid.render.statusPill(t(row.status)),
      },
      {
        key: "date_reported",
        name: t("DATE_REPORTED"),
        renderCell: ({ row }: Object) => formatDateTime(row.createdAt) || "",
      },
      {
        key: "reported_by",
        name: t("REPORTED_BY"),
        renderCell: ({ row }: Object) => row.reportedBy || "",
        align: "left",
      },
      {
        key: "date_closed",
        name: t("DATE_CLOSED"),
        renderCell: ({ row }: Object) => formatDateTime(row.closeAt) || "",
      },
      {
        key: "work_related_header",
        name: t("WORK_RELATED_HEADER"),
        renderCell: ({ row }: Object) => (row.isWorkRelated ? t("YES") : t("NO")),
        align: "left",
      },
      {
        key: "accident_related_header",
        name: t("ACCIDENT_RELATED_HEADER"),
        renderCell: ({ row }: Object) => (row.isAccidentRelated ? t("YES") : t("NO")),
        align: "left",
      },
      {
        key: "fit_date",
        name: t("FIT_DATE"),
        renderCell: ({ row }: Object) => formatDateTime(row.fitAt) || "",
      },
      {
        key: t("MANAGE"),
        frozen: "right",
        width: 60,
        name: t("MANAGE"),
        contextMenuConfig: row =>
          row.status === "OPEN" &&
          [
            {
              label: t("UPDATE"),
              disabled: !row.status === "OPEN",
              onClick: () => onUpdateAbsenceHandler(row),
            },
            {
              label: t("CLOSE"),
              disabled: !row.isCloseable,
              onClick: () => onCloseAbsenceHandler(row),
            },
          ].filter(item => item.disabled === false),
      },
    ];
  }

  function handleFiltersSubmit() {
    fetchMyAbsences();
  }

  function handleFiltersExport() {
    props.exportMyAbsences({
      clientId: props.clientId,
      employee: props.user,
    });
  }

  const onCloseAbsenceHandler = row => {
    setSelectedAbsence(row);
    props.setShowCloseModal(true);
  };

  const onReOpenAbsenceHandler = row => {
    setSelectedAbsence(row);
    props.setShowReOpenModal(true);
  };

  const onUpdateAbsenceHandler = row => {
    const {
      absenceType,
      startsAt,
      rtwAt,
      isAccidentRelated,
      isWorkRelated,
      hasConsentToShare,
      absenceReason,
    } = row;

    props.setMyCurrentAbsence(row);
    props.updateDraft({
      absenceType,
      startsAt,
      rtwAt,
      absenceReason: { id: absenceReason.id },
      isAccidentRelated,
      isWorkRelated,
      consentToShare: hasConsentToShare,
    });
    props.getTimeLost({
      clientId: props.clientId,
      absenceId: row.id,
    });
    props.setShowAddModal({ show: true, isEdit: true });
  };

  function canAddAbsence() {
    return props.canOpenAbsence;
  }

  return (
    <div className="">
      <div className="pb-4">
        <AbsencesFilter
          clientId={clientId}
          employee={user}
          schema={props.absencesSchema}
          onSubmit={handleFiltersSubmit}
          showAddAbsenceModal={() => {
            props.setShowAddModal({ show: true });
          }}
          canAddAbsence={canAddAbsence()}
          addButtonText={"REPORT_NEW_ABSENCE"}
          onExport={handleFiltersExport}
        />
      </div>
      <div>
        <GSGrid columns={columnDefs()} rows={myAbsences} />
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onChange={page => fetchMyAbsences(parseInt(page))}
        />
      </div>
      {props.showAddModal && (
        <AddMyAbsencesModal
          clientId={clientId}
          userId={user.id}
          isEdit={props.isEdit}
          onClose={() => {
            props.setShowAddModal({ show: false });
          }}
        />
      )}

      {props.showCloseModal && (
        <ConfirmationModal
          modalSize="normal"
          headerText={t("CLOSE_ABSENCE")}
          bodyContent={
            <div className="d-flex flex-column  align-items-center py-5">
              <h5>{t("CLOSE_ABSENCE_QUESTION")}</h5>
            </div>
          }
          onCancel={() => {
            props.setShowCloseModal(false);
            setSelectedAbsence(null);
          }}
          onConfirm={() => {
            props.setAbsenceStatus({
              clientId: props.clientId,
              absenceId: selectedAbsence.id,
              status: "CLOSED",
            });
          }}
          confirmBtnLabel={t("CONFIRM_CLOSE_ABSENCE")}
        />
      )}

      {props.showReOpenModal && (
        <ConfirmationModal
          modalSize="normal"
          headerText={t("RE_OPEN_ABSENCE")}
          bodyContent={
            <div className="d-flex flex-column align-items-center py-5">
              <h5>{t("RE_OPEN_ABSENCE_QUESTION")}</h5>
            </div>
          }
          onCancel={() => {
            props.setShowReOpenModal(false);
            setSelectedAbsence(null);
          }}
          onConfirm={() => {
            props.setAbsenceStatus({
              clientId: props.clientId,
              absenceId: selectedAbsence.id,
              status: "OPEN",
            });
          }}
          confirmBtnLabel={t("CONFIRM_RE_OPEN_ABSENCE")}
        />
      )}

      {props.showCancelModal && (
        <ConfirmationModal
          modalSize="normal"
          headerText={t("CANCEL_ABSENCE")}
          bodyContent={
            <div className="d-flex flex-column">
              <h4>{t("CANCEL_ABSENCE_QUESTION")}</h4>
            </div>
          }
          onCancel={() => {
            props.setShowCancelModal(false);
            setSelectedAbsence(null);
          }}
          onConfirm={() => {
            props.setAbsenceStatus({
              clientId: props.clientId,
              absenceId: props.currentAbsence?.id,
              status: "CANCELLED",
            });
          }}
          confirmBtnLabel={t("CONFIRM_CANCEL_ABSENCE")}
        />
      )}
    </div>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    myAbsences: selectors.getMyAbsences(state),
    pagination: selectors.getMyAbsencesPagination(state),
    absencesSchema: sharedSelectors.getAbsencesSchema(state),
    showAddModal: selectors.showAddMyAbsencesModal(state),
    isEdit: selectors.isEditMyAbsencesModal(state),
    showCloseModal: selectors.showCloseMyAbsencesModal(state),
    showReOpenModal: selectors.showReOpenMyAbsencesModal(state),
    showCancelModal: selectors.showCancelMyAbsencesModal(state),
    currentAbsence: selectors.myCurrentAbsence(state),
    canOpenAbsence: canOpenAbsence(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getMyAbsences: actionCreators.GetMyAbsences,
  getEmployeeAbsencesSchema: sharedActionCreators.GetEmployeeAbsencesSchema,
  setShowAddModal: actionCreators.SetShowAddMyAbsencesModal,
  setShowCloseModal: actionCreators.SetShowCloseMyAbsencesModal,
  setShowReOpenModal: actionCreators.SetShowReOpenMyAbsencesModal,
  setAbsenceStatus: actionCreators.SetAbsenceStatus,
  setShowCancelModal: actionCreators.SetShowCancelMyAbsencesModal,
  setMyCurrentAbsence: actionCreators.SetMyCurrentAbsence,
  updateDraft: actionCreators.SetMyAbsenceDraft,
  getTimeLost: actionCreators.GetMyAbsenceDaysLost,
  exportMyAbsences: actionCreators.ExportMyAbsences,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(MyAbsences));
